import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  createJournalEntry,
  processTransfer,
  getJournalEntries,
  type JournalEntryOutput,
} from '@/services/journal';
import { useTelegramBackButton } from '@/hooks/useTelegramBackButton';

interface JournalPageProps {
  onClose: () => void;
}

type View = 'list' | 'new' | 'transfer';

export function JournalPage({ onClose }: JournalPageProps) {
  const [view, setView] = useState<View>('list');
  const [entries, setEntries] = useState<JournalEntryOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // New entry form
  const [whatIDid, setWhatIDid] = useState('');
  const [whatWasNew, setWhatWasNew] = useState('');
  const [whatITake, setWhatITake] = useState('');
  const [nextStep, setNextStep] = useState('');

  // Transfer form
  const [transferEntryId, setTransferEntryId] = useState<string | null>(null);
  const [transferApplied, setTransferApplied] = useState(true);
  const [transferResult, setTransferResult] = useState('');

  // Telegram BackButton - go back to list or close
  const handleBack = useCallback(() => {
    if (view === 'list') {
      onClose();
    } else {
      setView('list');
    }
  }, [view, onClose]);
  useTelegramBackButton(handleBack);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const data = await getJournalEntries();
      setEntries(data.entries);
    } catch (error) {
      console.error('Failed to load journal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEntry = async () => {
    if (!whatIDid.trim() || !whatWasNew.trim() || !whatITake.trim() || !nextStep.trim()) {
      setMessage('Заполните все 4 поля');
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await createJournalEntry({
        whatIDid: whatIDid.trim(),
        whatWasNew: whatWasNew.trim(),
        whatITake: whatITake.trim(),
        nextStep: nextStep.trim(),
      });
      setWhatIDid('');
      setWhatWasNew('');
      setWhatITake('');
      setNextStep('');
      setView('list');
      setMessage('Запись сохранена!');
      await loadEntries();
    } catch (error: unknown) {
      setMessage((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitTransfer = async () => {
    if (!transferEntryId || !transferResult.trim()) {
      setMessage('Опишите результат');
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await processTransfer({
        entryId: transferEntryId,
        applied: transferApplied,
        result: transferResult.trim(),
      });
      setTransferEntryId(null);
      setTransferResult('');
      setView('list');
      setMessage('Трансфер записан!');
      await loadEntries();
    } catch (error: unknown) {
      setMessage((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openTransfer = (entryId: string) => {
    setTransferEntryId(entryId);
    setTransferApplied(true);
    setTransferResult('');
    setView('transfer');
    setMessage(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-950 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 rounded-full border-3 border-primary-700 border-t-primary-400"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-950 safe-area-top safe-area-bottom">
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-b border-primary-800/50">
        <div className="w-16" />
        <h1 className="text-white font-medium">
          {view === 'new' ? 'Новая запись' : view === 'transfer' ? 'Трансфер' : 'Свиток'}
        </h1>
        {view === 'list' ? (
          <button
            onClick={() => { setView('new'); setMessage(null); }}
            className="w-16 text-indigo-400 text-sm font-medium text-right"
          >
            + Запись
          </button>
        ) : (
          <div className="w-16" />
        )}
      </header>

      {/* Message */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pt-3"
          >
            <div className="rounded-lg bg-primary-800/50 border border-primary-700/50 p-2">
              <p className="text-sm text-primary-300 text-center">{message}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New entry form */}
      {view === 'new' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 space-y-4"
        >
          <JournalField
            label="Что я сделал(а)?"
            emoji="🎯"
            value={whatIDid}
            onChange={setWhatIDid}
            placeholder="Опишите действие или практику..."
          />
          <JournalField
            label="Что было новым?"
            emoji="💡"
            value={whatWasNew}
            onChange={setWhatWasNew}
            placeholder="Какой новый опыт или инсайт..."
          />
          <JournalField
            label="Что я забираю с собой?"
            emoji="🎒"
            value={whatITake}
            onChange={setWhatITake}
            placeholder="Главный вывод или навык..."
          />
          <JournalField
            label="Следующий шаг"
            emoji="👣"
            value={nextStep}
            onChange={setNextStep}
            placeholder="Что конкретно сделаю дальше..."
          />

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleSubmitEntry}
            disabled={saving}
            className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm disabled:opacity-50 mt-2"
          >
            {saving ? 'Сохраняю...' : 'Сохранить запись'}
          </motion.button>
        </motion.div>
      )}

      {/* Transfer form */}
      {view === 'transfer' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 space-y-4"
        >
          <p className="text-sm text-primary-400">
            Прошло 48 часов. Удалось ли применить то, что вы забрали с собой?
          </p>

          <div className="flex gap-2">
            <button
              onClick={() => setTransferApplied(true)}
              className={`flex-1 py-3 rounded-xl text-sm font-medium border ${
                transferApplied
                  ? 'bg-green-900/30 border-green-700/50 text-green-300'
                  : 'bg-primary-800/30 border-primary-700/50 text-primary-400'
              }`}
            >
              Да, применил(а)
            </button>
            <button
              onClick={() => setTransferApplied(false)}
              className={`flex-1 py-3 rounded-xl text-sm font-medium border ${
                !transferApplied
                  ? 'bg-amber-900/30 border-amber-700/50 text-amber-300'
                  : 'bg-primary-800/30 border-primary-700/50 text-primary-400'
              }`}
            >
              Нет, не получилось
            </button>
          </div>

          <div>
            <label className="text-sm text-primary-400 mb-1 block">
              {transferApplied ? 'Что получилось?' : 'Что помешало?'}
            </label>
            <textarea
              value={transferResult}
              onChange={(e) => setTransferResult(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Опишите результат..."
              className="w-full bg-primary-800/50 border border-primary-700/50 rounded-xl p-3 text-white text-sm placeholder-primary-600 resize-none focus:outline-none focus:border-indigo-500/50"
            />
            <p className="text-xs text-primary-600 text-right mt-1">{transferResult.length}/500</p>
          </div>

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleSubmitTransfer}
            disabled={saving}
            className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm disabled:opacity-50"
          >
            {saving ? 'Сохраняю...' : 'Записать трансфер'}
          </motion.button>
        </motion.div>
      )}

      {/* Entries list */}
      {view === 'list' && (
        <div className="p-4 space-y-3">
          {entries.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-4xl block mb-3">📝</span>
              <p className="text-primary-400 text-sm">Дневник пуст</p>
              <p className="text-primary-600 text-xs mt-1">Запишите свой первый опыт</p>
            </div>
          ) : (
            entries.map((entry, i) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="rounded-xl bg-primary-800/30 border border-primary-700/50 p-3"
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="text-xs text-primary-500">
                    {new Date(entry.createdAt).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  {entry.transferCheckedAt && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      entry.transferApplied
                        ? 'bg-green-900/30 text-green-400'
                        : 'bg-amber-900/30 text-amber-400'
                    }`}>
                      {entry.transferApplied ? 'Трансфер' : 'Не перенёс'}
                    </span>
                  )}
                  {entry.needsTransferCheck && (
                    <button
                      onClick={() => openTransfer(entry.id)}
                      className="text-xs px-2 py-0.5 rounded bg-indigo-900/30 text-indigo-400 border border-indigo-800/30"
                    >
                      Проверить трансфер
                    </button>
                  )}
                </div>

                <div className="space-y-1.5">
                  {entry.whatIDid && (
                    <p className="text-sm text-white">
                      <span className="text-primary-500">Что сделал: </span>{entry.whatIDid}
                    </p>
                  )}
                  {entry.whatITake && (
                    <p className="text-sm text-primary-300">
                      <span className="text-primary-500">Забираю: </span>{entry.whatITake}
                    </p>
                  )}
                  {entry.nextStep && (
                    <p className="text-sm text-primary-400">
                      <span className="text-primary-500">Шаг: </span>{entry.nextStep}
                    </p>
                  )}
                </div>

                {entry.transferResult && (
                  <div className="mt-2 pt-2 border-t border-primary-700/30">
                    <p className="text-xs text-primary-400">
                      <span className="text-primary-500">Трансфер: </span>{entry.transferResult}
                    </p>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function JournalField({
  label,
  emoji,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  emoji: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-sm text-primary-300 mb-1.5">
        <span>{emoji}</span> {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        rows={2}
        placeholder={placeholder}
        className="w-full bg-primary-800/50 border border-primary-700/50 rounded-xl p-3 text-white text-sm placeholder-primary-600 resize-none focus:outline-none focus:border-indigo-500/50"
      />
      <p className="text-xs text-primary-600 text-right mt-0.5">{value.length}/500</p>
    </div>
  );
}
